import React, { useEffect, useMemo, useState } from 'react';
import { FaBroadcastTower, FaCheckCircle, FaClock, FaLink, FaPlus, FaSpinner, FaTimes } from 'react-icons/fa';
import GiftSetService from '../../services/GiftSetService';

const formatTimeLeft = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.ceil(Number(milliseconds || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const GiftSetPairingPanel = ({
  isPairing,
  ownerUuid,
  pendingBind,
  onAddMemberFromUrl,
  onStartPairing,
  onStopPairing,
  theme,
}) => {
  const [now, setNow] = useState(Date.now());
  const [memberUrl, setMemberUrl] = useState('');
  const [memberName, setMemberName] = useState('');
  const [manualError, setManualError] = useState('');
  const [isAddingManual, setIsAddingManual] = useState(false);
  const isActive = pendingBind?.ownerUuid === ownerUuid;
  const timeLeft = useMemo(() => Number(pendingBind?.expiresAt || 0) - now, [now, pendingBind]);
  const parsedUuid = useMemo(() => GiftSetService.parseUuidFromInput(memberUrl), [memberUrl]);

  useEffect(() => {
    if (!isActive) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isActive]);

  const handleManualAdd = async (event) => {
    event.preventDefault();
    if (!onAddMemberFromUrl || !parsedUuid) return;
    setManualError('');
    setIsAddingManual(true);
    try {
      await onAddMemberFromUrl({
        memberUrl,
        nickname: memberName || `NFC ${GiftSetService.formatShortUuid(parsedUuid)}`,
      });
      setMemberUrl('');
      setMemberName('');
    } catch (error) {
      setManualError(error.message || 'This NFC could not be added.');
    } finally {
      setIsAddingManual(false);
    }
  };

  return (
    <div className="mt-4 rounded-[1.25rem] border p-4" style={{ borderColor: isActive ? theme.accent : theme.cardBorder, backgroundColor: isActive ? theme.accentSoft : theme.cardBg }}>
      <div className="flex items-start gap-3">
        <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-[1.15rem]" style={{ backgroundColor: isActive ? theme.cardBg : theme.accentSoft, color: theme.accent }}>
          {isActive && <span className="absolute inset-0 rounded-[1.15rem] animate-ping opacity-30" style={{ backgroundColor: theme.accent }} />}
          {isActive ? <FaBroadcastTower className="relative" /> : <FaPlus />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-black uppercase tracking-[0.2em]" style={{ color: theme.accent }}>
            {isActive ? 'Waiting for NFC' : 'Add Member NFC'}
          </p>
          <h4 className="mt-1 text-sm font-black" style={{ color: theme.text }}>
            {isActive ? `Scan another Lumirae NFC for ${pendingBind.groupName || 'this Gift Set'}` : 'Start a secure scan session'}
          </h4>
          <p className="mt-2 text-xs font-bold leading-relaxed" style={{ color: theme.secondaryText || theme.text }}>
            {isActive ?
              'Tap a new NFC with this phone. The opened page will identify the NFC and ask for a member name.' :
              'This creates a short-lived pairing session. Only registered, available Lumirae NFCs can be added.'}
          </p>
        </div>
      </div>

      {isActive ? (
        <>
          <div className="mt-4 grid grid-cols-3 gap-2">
            {[
              ['1', 'Owner verified'],
              ['2', 'Scan member'],
              ['3', 'Name and add'],
            ].map(([step, label], index) => (
              <div key={step} className="rounded-[0.85rem] border px-3 py-2 text-center" style={{ borderColor: theme.cardBorder, backgroundColor: index === 1 ? theme.cardBg : 'rgba(255,255,255,0.35)' }}>
                <div className="mx-auto flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-black" style={{ backgroundColor: index === 1 ? theme.accent : theme.accentSoft, color: index === 1 ? '#fff' : theme.accent }}>
                  {index === 0 ? <FaCheckCircle /> : step}
                </div>
                <div className="mt-1 truncate text-[8px] font-black uppercase tracking-tight" style={{ color: theme.text }}>
                  {label}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between rounded-[0.95rem] border px-3 py-2" style={{ borderColor: theme.cardBorder, backgroundColor: theme.cardBg }}>
            <span className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest" style={{ color: theme.secondaryText }}>
              <FaClock /> Session
            </span>
            <span className="text-xs font-black" style={{ color: timeLeft > 0 ? theme.accent : theme.stateError || theme.accent }}>
              {timeLeft > 0 ? `${formatTimeLeft(timeLeft)} left` : 'Expired'}
            </span>
          </div>

          <form onSubmit={handleManualAdd} className="mt-4 rounded-[1rem] border p-3" style={{ borderColor: theme.cardBorder, backgroundColor: theme.cardBg }}>
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: theme.accentSoft, color: theme.accent }}>
                <FaLink />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] font-black uppercase tracking-widest" style={{ color: theme.accent }}>
                  Paste NFC URL
                </p>
                <p className="mt-1 text-[10px] font-bold leading-relaxed opacity-60">
                  If scanning opens a different browser, paste the NFC link here. The server still verifies the UUID exists and is available.
                </p>
              </div>
            </div>
            <input
              value={memberUrl}
              onChange={(event) => setMemberUrl(event.target.value)}
              className="lumirae-ds-input mt-3 w-full p-3 text-xs font-bold"
              placeholder="https://memories.thelumirae.com/NFC_UUID"
            />
            <input
              value={memberName}
              onChange={(event) => setMemberName(event.target.value)}
              className="lumirae-ds-input mt-2 w-full p-3 text-xs font-bold"
              placeholder={parsedUuid ? `Name: NFC ${GiftSetService.formatShortUuid(parsedUuid)}` : 'Member name, e.g. Candle jar 02'}
            />
            {manualError && (
              <p className="mt-2 text-[10px] font-bold leading-relaxed" style={{ color: theme.stateError || '#B42318' }}>
                {manualError}
              </p>
            )}
            <button
              type="submit"
              disabled={!parsedUuid || isAddingManual || timeLeft <= 0}
              className="lumirae-ds-button lumirae-ds-button-primary mt-3 flex w-full items-center justify-center gap-2 px-5 py-3 text-[10px] disabled:opacity-50"
            >
              {isAddingManual ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
              Add this NFC
            </button>
          </form>

          <button
            type="button"
            onClick={onStopPairing}
            className="lumirae-ds-button lumirae-ds-button-secondary mt-4 flex w-full items-center justify-center gap-2 px-5 py-4 text-[10px]"
          >
            <FaTimes /> Stop waiting
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={onStartPairing}
          disabled={isPairing}
          className="lumirae-ds-button lumirae-ds-button-primary mt-4 flex w-full items-center justify-center gap-2 px-5 py-4 disabled:opacity-60"
        >
          {isPairing ? <FaSpinner className="animate-spin" /> : <FaPlus />}
          Start NFC pairing
        </button>
      )}
    </div>
  );
};

export default GiftSetPairingPanel;
